import type { HTMLAttributes } from "react";
import { cx } from "@/utils/cx";

export interface EveMindLogoProps extends HTMLAttributes<HTMLDivElement> {
    showText?: boolean;
}

export const EveMindLogo = ({ showText = true, className, ...props }: EveMindLogoProps) => {
    return (
        <div {...props} className={cx("flex items-center gap-3", className)}>
            {/* Logo Icon - Stylized teardrop with thick outline and sparkles */}
            <div className="relative flex h-10 w-10 shrink-0 items-center justify-center">
                <svg
                    viewBox="0 0 100 100"
                    fill="none"
                    className="h-full w-full"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <defs>
                        {/* Metallic gradient for the outline */}
                        <linearGradient id="logoOutlineGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#E5E7EB" stopOpacity="1" />
                            <stop offset="50%" stopColor="#F9FAFB" stopOpacity="1" />
                            <stop offset="100%" stopColor="#D1D5DB" stopOpacity="1" />
                        </linearGradient>
                        {/* Glow for blue sparkle */}
                        <filter id="blueGlow">
                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                        {/* Glow for green sparkle */}
                        <filter id="greenGlow">
                            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
                            <feMerge>
                                <feMergeNode in="coloredBlur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>
                    </defs>
                    
                    {/* Main teardrop shape with thick outline - rounded D shape */}
                    <path
                        d="M50 20C50 20 22 40 22 65C22 80 35 88 50 88C65 88 78 80 78 65C78 40 50 20 50 20Z"
                        stroke="url(#logoOutlineGradient)"
                        strokeWidth="6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                    />
                    
                    {/* Blue sparkle (larger, positioned left and below center) */}
                    <g transform="translate(36, 48)" filter="url(#blueGlow)">
                        <path
                            d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z"
                            fill="#60A5FA"
                            fillOpacity="0.9"
                        />
                    </g>
                    
                    {/* Green sparkle (smaller, positioned right and above blue) */}
                    <g transform="translate(46, 42)" filter="url(#greenGlow)">
                        <path
                            d="M3.5 0L4.2 3L7.5 3.5L4.2 4L3.5 7L2.8 4L0 3.5L2.8 3Z"
                            fill="#34D399"
                            fillOpacity="0.9"
                        />
                    </g>
                </svg>
            </div>
            
            {/* Logo Text */}
            {showText && (
                <span className="text-xl font-semibold text-primary">EveMind</span>
            )}
        </div>
    );
};

